import React, { useState } from 'react';
import { FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';

const SECRET_TYPES = {
  CREDIT_CARD: {
    cardName: '',
    variant: '',
    cardNumber: '',
    cardHolderName: '',
    expiryDate: '',
    cvv: ''
  },
  BANK_ACCOUNT: {
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    bankName: '',
    branch: ''
  },
  AADHAR_CARD: {
    number: '',
    name: '',
    dob: '',
    address: '',
    gender: ''
  },
  PAN_CARD: {
    number: '',
    name: '',
    fatherName: '',
    dob: ''
  },
  DRIVING_LICENSE: {
    number: '',
    name: '',
    validFrom: '',
    validUntil: '',
    vehicleClasses: '',
    issuingAuthority: ''
  },
  OTHER: {
    title: '',
    username: '',
    password: '',
    notes: ''
  }
};

const documentFields = {
  AADHAR_CARD: [
    { name: 'number', label: 'Aadhar Number', type: 'text', required: true },
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'dob', label: 'Date of Birth', type: 'date', required: true },
    { name: 'address', label: 'Address', type: 'textarea', required: true },
    { name: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true }
  ],
  PAN_CARD: [
    { name: 'number', label: 'PAN Number', type: 'text', required: true },
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'fatherName', label: "Father's Name", type: 'text', required: true },
    { name: 'dob', label: 'Date of Birth', type: 'date', required: true }
  ],
  DRIVING_LICENSE: [
    { name: 'number', label: 'License Number', type: 'text', required: true },
    { name: 'name', label: 'Full Name', type: 'text', required: true },
    { name: 'validFrom', label: 'Valid From', type: 'date', required: true },
    { name: 'validUntil', label: 'Valid Until', type: 'date', required: true },
    { name: 'vehicleClasses', label: 'Vehicle Classes', type: 'text', required: true },
    { name: 'issuingAuthority', label: 'Issuing Authority', type: 'text', required: true }
  ]
};

const AddSecretModal = ({ isOpen, onClose, onSave, loading }) => {
  const [type, setType] = useState('CREDIT_CARD');
  const [formData, setFormData] = useState(SECRET_TYPES.CREDIT_CARD);
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    
    try {
      await onSave({
        type,
        data: formData
      }, secretKey);
      
      // Reset form
      setFormData(SECRET_TYPES[type]);
      setSecretKey('');
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setType(newType);
    setFormData(SECRET_TYPES[newType]);
  };

  const renderFormFields = () => {
    const fields = Object.entries(formData);
    return fields.map(([field, value]) => (
      <div key={field} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.replace(/([A-Z])/g, ' $1')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')}
        </label>
        {field === 'notes' || field === 'address' ? (
          <textarea
            name={field}
            value={value}
            onChange={handleInputChange}
            className="w-full p-2 border rounded-md"
            rows={3}
            required
          />
        ) : field === 'gender' ? (
          <select
            name={field}
            value={value}
            onChange={handleInputChange}
            className="w-full p-2 border rounded-md"
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        ) : (
          <input
            type={field.includes('password') || field === 'cvv' ? 'password' 
              : field.includes('date') || field.includes('dob') || field.includes('valid') ? 'date'
              : 'text'}
            name={field}
            value={value}
            onChange={handleInputChange}
            className="w-full p-2 border rounded-md"
            required
          />
        )}
      </div>
    ));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Secret</h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Secret Type</label>
            <select
              value={type}
              onChange={handleTypeChange}
              className="w-full p-2 border rounded"
            >
              {Object.keys(SECRET_TYPES).map(type => (
                <option key={type} value={type}>
                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          {renderFormFields()}

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Encryption Key
            </label>
            <div className="relative">
              <input
                type={showSecretKey ? 'text' : 'password'}
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
                placeholder="Enter your secret key"
                className="w-full p-2 border rounded-md pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowSecretKey(!showSecretKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showSecretKey ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Secret'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddSecretModal;
